// QueueV2Setup — the white, hospital-facing setup step for the v2 display.
//
// Lets staff (1) pick which doctors appear and (2) choose the layout
// (Column or Slideshow), then launches the live display. Choices persist
// per hospital so a rebooted TV comes straight back to the same display.
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Logo, Logout } from "../../assets/icons/Icons";
import { useHospDocData } from "../../lib/contexts/HospitalDoctorContext";
import { useUserData } from "../../lib/contexts/UserContext";
import { Doctor } from "../../lib/utils/types";
import { formatDoctorName } from "../../lib/utils/formatDoctorName";
import { setAudioLanguageToLocalStorage } from "../../lib/utils/speechUtils";
import { ANNOUNCEMENT_LANGUAGES } from "../utils/audioLanguage";
import {
  DEFAULT_SELECTION,
  ViewMode,
  loadSelection,
  saveSelection,
} from "../utils/selection";

const ColumnGlyph: React.FC<{ active: boolean }> = ({ active }) => {
  const c = active ? "#0B74FF" : "#C2CBDA";
  return (
    <svg width="56" height="40" viewBox="0 0 56 40" fill="none">
      {[4, 22, 40].map((x) => (
        <rect key={x} x={x} y="4" width="12" height="32" rx="2" fill={c} />
      ))}
    </svg>
  );
};

const SlideshowGlyph: React.FC<{ active: boolean }> = ({ active }) => {
  const c = active ? "#0B74FF" : "#C2CBDA";
  return (
    <svg width="56" height="40" viewBox="0 0 56 40" fill="none">
      <rect x="14" y="6" width="28" height="28" rx="3" fill={c} />
      <rect x="6" y="12" width="6" height="16" rx="2" fill={c} opacity="0.45" />
      <rect x="44" y="12" width="6" height="16" rx="2" fill={c} opacity="0.45" />
    </svg>
  );
};

const initials = (name?: string) => {
  if (!name) return "Dr";
  const p = name.replace(/^dr\.?\s*/i, "").trim().split(/\s+/);
  return ((p[0]?.[0] ?? "") + (p[1]?.[0] ?? "")).toUpperCase() || "Dr";
};

const QueueV2Setup: React.FC = () => {
  const { hospital_id } = useParams();
  const navigate = useNavigate();
  const { allDoctorsData, setHospitalID } = useHospDocData();
  const { handleLogout } = useUserData();

  const doctors = useMemo<Doctor[]>(
    () => (allDoctorsData ?? []).filter((d) => d && d.mapping_id && !d.hide_from_tv),
    [allDoctorsData]
  );

  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [viewMode, setViewMode] = useState<ViewMode>(DEFAULT_SELECTION.viewMode);
  const [intervalSec, setIntervalSec] = useState(DEFAULT_SELECTION.intervalSec);
  const [ads, setAds] = useState(DEFAULT_SELECTION.ads);
  const [language, setLanguage] = useState(DEFAULT_SELECTION.language);

  useEffect(() => {
    if (hospital_id) setHospitalID(hospital_id);
  }, [hospital_id, setHospitalID]);

  // Rehydrate any previous selection once doctors are known.
  useEffect(() => {
    if (!hospital_id || doctors.length === 0) return;
    const prev = loadSelection(hospital_id);
    if (prev) {
      const valid = new Set(doctors.map((d) => d.mapping_id));
      setSelected(new Set(prev.mappingIds.filter((id) => valid.has(id))));
      setViewMode(prev.viewMode);
      setIntervalSec(prev.intervalSec);
      setAds(prev.ads);
      setLanguage(prev.language);
    }
  }, [hospital_id, doctors]);

  const toggle = (id: string) =>
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });

  const allSelected = doctors.length > 0 && selected.size === doctors.length;
  const toggleAll = () =>
    setSelected(allSelected ? new Set() : new Set(doctors.map((d) => d.mapping_id)));

  const canStart = selected.size > 0;

  // Best-effort: request real browser fullscreen for the kiosk. This MUST run
  // from inside the click handler — browsers only grant fullscreen during a
  // user gesture. It can still reject (iframe without allow="fullscreen",
  // user/policy blocked), so we swallow errors and navigate either way.
  const goFullscreen = async () => {
    const el = document.documentElement as any;
    const req =
      el.requestFullscreen ||
      el.webkitRequestFullscreen || // Safari
      el.msRequestFullscreen; // legacy Edge/IE
    if (!req || document.fullscreenElement) return;
    try {
      await req.call(el);
    } catch {
      /* fullscreen not permitted — display still fills the window */
    }
  };

  const start = () => {
    if (!hospital_id || !canStart) return;
    const ordered = doctors
      .map((d) => d.mapping_id)
      .filter((id) => selected.has(id));
    saveSelection(hospital_id, {
      mappingIds: ordered,
      viewMode,
      intervalSec,
      ads,
      language,
    });
    // Same step the profile path takes — the announcer reads the language from
    // localStorage, so it has to be written before the display mounts.
    setAudioLanguageToLocalStorage(language);
    // Fire fullscreen from this gesture, then navigate (don't await — a reject
    // shouldn't block the display from opening).
    void goFullscreen();
    navigate(`/${hospital_id}/v2/display`);
  };

  return (
    <div className="min-h-screen w-full bg-[#F7F9FC]">
      {/* Header */}
      <div className="flex items-center justify-between border-b border-[#E9EDF3] bg-white px-8 py-4">
        <div className="flex items-center gap-3">
          <Logo />
          <span className="text-sm font-medium text-[#7A869A]">
            Live Queue · Setup
          </span>
        </div>
        <button
          onClick={handleLogout}
          className="flex items-center gap-1.5 rounded-full border border-onLeave px-4 py-1.5 text-sm text-onLeave hover:bg-onLeave hover:text-white hover:border-red-400"
        >
          <Logout />
          Logout
        </button>
      </div>

      <div className="mx-auto max-w-5xl px-6 py-10">
        {/* Back to the profile chooser this page is reached from. Boxy
            (rounded-lg) like the dashboard's buttons and the selects below. */}
        <button
          onClick={() => navigate(`/${hospital_id}/v2`)}
          className="mb-6 flex items-center gap-1.5 rounded-lg border border-[#E3E8F0] bg-white px-3.5 py-2 text-sm font-medium text-[#1F2B4D] shadow-sm hover:border-[#0B74FF] hover:text-[#0B74FF]"
        >
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth={2.2}
            strokeLinecap="round"
            strokeLinejoin="round"
            className="h-4 w-4"
          >
            <path d="M15 18l-6-6 6-6" />
          </svg>
          Back to profiles
        </button>

        <h1 className="text-3xl font-semibold text-[#1F2B4D]">
          Set up your display
        </h1>
        <p className="mt-2 text-[#7A869A]">
          Choose the doctors to show and how the waiting-room screen should look.
        </p>

        {/* Layout choice */}
        <section className="mt-8">
          <h2 className="mb-3 text-sm font-semibold uppercase tracking-wider text-[#9AA5B8]">
            Layout
          </h2>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            {([
              {
                mode: "column" as const,
                title: "Column View",
                desc: "Two doctors side-by-side; rotates through the rest.",
                Glyph: ColumnGlyph,
              },
              {
                mode: "slideshow" as const,
                title: "Slideshow View",
                desc: "One doctor per screen on a large panel; rotates.",
                Glyph: SlideshowGlyph,
              },
            ]).map(({ mode, title, desc, Glyph }) => {
              const active = viewMode === mode;
              return (
                <button
                  key={mode}
                  onClick={() => setViewMode(mode)}
                  className={`flex items-start gap-4 rounded-2xl border bg-white p-5 text-left transition-all ${
                    active
                      ? "border-[#0B74FF] ring-2 ring-[#0B74FF]/15 shadow-[0_4px_16px_rgba(11,116,255,0.12)]"
                      : "border-[#E9EDF3] hover:border-[#C9D6EA]"
                  }`}
                >
                  <Glyph active={active} />
                  <div>
                    <p className="font-semibold text-[#1F2B4D]">{title}</p>
                    <p className="mt-1 text-sm text-[#7A869A]">{desc}</p>
                  </div>
                </button>
              );
            })}
          </div>

          <div className="mt-4 flex flex-wrap items-center gap-6 rounded-xl bg-white p-4 ring-1 ring-[#E9EDF3]">
            <label className="flex items-center gap-3 text-sm text-[#5A6478]">
              Rotate every
              <select
                value={intervalSec}
                onChange={(e) => setIntervalSec(Number(e.target.value))}
                className="rounded-lg border border-[#E3E8F0] bg-white px-3 py-1.5 text-[#1F2B4D]"
              >
                {[5, 8, 10, 15, 20, 30].map((n) => (
                  <option key={n} value={n}>
                    {n}s
                  </option>
                ))}
              </select>
            </label>
            <label className="flex items-center gap-3 text-sm text-[#5A6478]">
              Announce in
              <select
                value={language}
                onChange={(e) => setLanguage(e.target.value)}
                className="rounded-lg border border-[#E3E8F0] bg-white px-3 py-1.5 text-[#1F2B4D]"
              >
                {ANNOUNCEMENT_LANGUAGES.map((l) => (
                  <option key={l.code} value={l.code}>
                    {l.label}
                  </option>
                ))}
              </select>
            </label>
            <label className="flex cursor-pointer items-center gap-2 text-sm text-[#5A6478]">
              <input
                type="checkbox"
                checked={ads}
                onChange={(e) => setAds(e.target.checked)}
                className="h-4 w-4 accent-[#0B74FF]"
              />
              Show ads panel
            </label>
            <span className="text-xs text-[#9AA5B8]">
              {viewMode === "slideshow"
                ? "One doctor shown at a time."
                : "Two doctors shown at a time."}{" "}
              Rotation applies only when more doctors are selected than fit on
              screen.
            </span>
          </div>
        </section>

        {/* Doctor selection */}
        <section className="mt-10">
          <div className="mb-3 flex items-center justify-between">
            <h2 className="text-sm font-semibold uppercase tracking-wider text-[#9AA5B8]">
              Doctors {selected.size > 0 && `· ${selected.size} selected`}
            </h2>
            {doctors.length > 0 && (
              <button
                onClick={toggleAll}
                className="text-sm font-medium text-[#0B74FF] hover:underline"
              >
                {allSelected ? "Clear all" : "Select all"}
              </button>
            )}
          </div>

          {doctors.length === 0 ? (
            <div className="rounded-2xl bg-white p-10 text-center text-[#7A869A] ring-1 ring-[#E9EDF3]">
              No doctors available for this hospital.
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {doctors.map((doc) => {
                const active = selected.has(doc.mapping_id);
                return (
                  <button
                    key={doc.mapping_id}
                    onClick={() => toggle(doc.mapping_id)}
                    className={`flex items-center gap-3 rounded-xl border bg-white p-3 text-left transition-all ${
                      active
                        ? "border-[#0B74FF] ring-2 ring-[#0B74FF]/15"
                        : "border-[#E9EDF3] hover:border-[#C9D6EA]"
                    }`}
                  >
                    {doc.profile_picture ? (
                      <img
                        src={doc.profile_picture}
                        alt={doc.full_name}
                        className="h-11 w-11 rounded-full object-cover ring-1 ring-[#E5E9F0]"
                      />
                    ) : (
                      <div className="flex h-11 w-11 items-center justify-center rounded-full bg-[#EAF2FF] text-sm font-semibold text-[#0B74FF] ring-1 ring-[#D7E6FF]">
                        {initials(doc.full_name)}
                      </div>
                    )}
                    <div className="min-w-0 flex-1">
                      <p className="truncate font-medium text-[#1F2B4D]">
                        {formatDoctorName(doc.full_name, doc.show_name_prefix !== false)}
                      </p>
                      {doc.specialization && (
                        <p className="truncate text-xs text-[#7A869A]">
                          {doc.specialization}
                        </p>
                      )}
                    </div>
                    <span
                      className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-md border ${
                        active
                          ? "border-[#0B74FF] bg-[#0B74FF] text-white"
                          : "border-[#CDD6E4] bg-white"
                      }`}
                    >
                      {active && (
                        <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                          <path
                            d="M2.5 6.5L5 9L9.5 3.5"
                            stroke="currentColor"
                            strokeWidth="1.8"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      )}
                    </span>
                  </button>
                );
              })}
            </div>
          )}
        </section>
      </div>

      {/* Sticky launch bar */}
      <div className="sticky bottom-0 border-t border-[#E9EDF3] bg-white/90 backdrop-blur">
        <div className="mx-auto flex max-w-5xl items-center justify-between px-6 py-4">
          <p className="text-sm text-[#7A869A]">
            {canStart
              ? `${selected.size} doctor${selected.size > 1 ? "s" : ""} · ${
                  viewMode === "column" ? "Column" : "Slideshow"
                } view · ${
                  ANNOUNCEMENT_LANGUAGES.find((l) => l.code === language)?.label ??
                  "English"
                } announcements`
              : "Select at least one doctor to continue"}
          </p>
          <button
            onClick={start}
            disabled={!canStart}
            className={`rounded-full px-8 py-3 text-sm font-semibold text-white transition-colors ${
              canStart
                ? "bg-[#0B74FF] hover:bg-[#0a66e0]"
                : "cursor-not-allowed bg-[#BBC6D8]"
            }`}
          >
            Start display
          </button>
        </div>
      </div>
    </div>
  );
};

export default QueueV2Setup;
